<template>
  <div>
    <el-radio-group v-if="!isDesktop" v-model="viewType" class="padding-5">
      <el-radio-button key="Mobile" value="Mobile"> Mobile </el-radio-button>
      <el-radio-button key="Full" value="Full"> Full view </el-radio-button>
    </el-radio-group>
    <el-card
      :class="{ mobile: viewType === 'Mobile' && !isDesktop }"
      class="padding-5"
    >
      <el-table
        v-show="isDesktop || viewType === 'Full'"
        ref="table"
        v-loading="loading"
        :data="items"
        highlight-current-row
        show-summary
        :summary-method="getSummaries"
        size="small"
        @sort-change="applySorting"
      >
        <el-table-column align="center" label="Site" prop="domain" width="200">
          <template #default="{ row }">
            <b>{{ row.attributes.domain }}</b>
            <div class="info-icons">
              <AvailabilityLabel
                v-model="row.attributes.availability"
                class="info-icon-item"
              />
              <PublisherTypeLabel
                v-model="row.attributes.external"
                class="info-icon-item"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column
          v-if="showSizeColumn"
          align="center"
          label="Size"
          sortable="custom"
          :formatter="formatter"
          prop="size"
        />
        <el-table-column align="center" label="Quality" prop="quality">
          <template #default="{ row }">
            <TrafficLabel
              :title="row.attributes.quality_title"
              :quality="row.attributes.quality"
            />
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          label="Stream"
          :formatter="formatter"
          prop="stream"
        />
        <el-table-column
          align="center"
          label="GEO"
          :formatter="formatter"
          prop="country"
          width="50"
        >
          <template #default="{ row }">
            <el-tooltip
              class="item"
              effect="dark"
              placement="top-start"
              :content="row.attributes.country"
            >
              <img
                :src="getFlagSrc(row.attributes.country_code)"
                width="20px;"
              />
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          label="Device"
          :formatter="formatter"
          prop="device"
        />
        <el-table-column
          align="center"
          label="Volumes"
          :formatter="formatter"
          prop="traffic"
          sortable="custom"
        >
          <template #header>
            Volumes
            <IconWithHint> The number of Ad impressions per day </IconWithHint>
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          label="Spot"
          :formatter="formatter"
          prop="name"
        />
        <el-table-column
          align="center"
          label="Alexa rank"
          sortable="custom"
          :formatter="formatter"
          prop="rank"
        />
        <el-table-column align="left" label="Spot info" width="200">
          <template #header>
            <el-row type="flex" justify="space-between">
              <span>Spot info</span>
              <ListButton
                :disabled="listButtonState.disabled"
                :tooltip="listButtonState.tooltip"
                @click="openModal"
              />
            </el-row>
          </template>
          <template #default="{ row }">
            <span v-html="row.attributes.info.join('<br/>')" />
          </template>
        </el-table-column>
        <el-table-column align="center">
          <template #header>
            <ExportButton @click="download" />
          </template>
          <template #default="{ row }">
            <el-tooltip
              v-if="false"
              class="item"
              placement="top-start"
              content="Request details"
            >
              <el-button
                type="primary"
                size="small"
                circle
                icon="MessageElementIcon"
                @click="showMessagePopup(row)"
              />
            </el-tooltip>
            <el-tooltip
              v-if="row.attributes.availability"
              class="item"
              placement="top-start"
              content="Buy traffic"
            >
              <el-button
                type="success"
                size="small"
                circle
                icon="MoneyElementIcon"
                @click="showCampaignsPopup(row.id)"
              />
            </el-tooltip>
            <el-tooltip
              v-if="row.attributes.availability"
              class="item"
              placement="top-start"
              content="Offer flat deal"
            >
              <el-button
                type="warning"
                size="small"
                circle
                icon="WalletFilledElementIcon"
                @click="showFlatDealForm(row)"
              />
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
      <el-table
        v-show="viewType === 'Mobile' && !isDesktop"
        ref="table"
        v-loading="loading"
        :data="items"
        highlight-current-row
        show-summary
        :summary-method="getSummaries"
        size="small"
        class="mobile"
        @sort-change="applySorting"
      >
        <el-table-column
          align="center"
          label="Site"
          prop="availability"
          width="150"
          class-name="site"
        >
          <template #default="{ row }">
            <b>{{ row.attributes.domain }}</b>
            <div class="info-icons">
              <AvailabilityLabel
                v-model="row.attributes.availability"
                class="info-icon-item"
              />
              <PublisherTypeLabel
                v-model="row.attributes.external"
                class="info-icon-item"
              />
              <TrafficLabel
                :title="row.attributes.quality_title"
                :quality="row.attributes.quality"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          label="Properties"
          prop="quality"
          class-name="site"
          :width="isMobileViewport ? 40 : 'auto'"
        >
          <template #default="{ row }">
            <b>{{ row.attributes.stream }}</b>
            <br />
            <el-tooltip
              class="item"
              effect="dark"
              placement="top-start"
              :content="row.attributes.country"
            >
              <img
                :src="getFlagSrc(row.attributes.country_code)"
                width="20px;"
              />
            </el-tooltip>
            <br />
            <span>{{ row.attributes.device }}</span>
            <br />
            <b v-if="showSizeColumn">{{ row.attributes.size }}</b>
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          label="Volumes"
          :formatter="formatter"
          prop="traffic"
          sortable="custom"
          class-name="site"
          :width="isMobileViewport ? 40 : ''"
        >
          <template #header>
            Volumes
            <IconWithHint> The number of Ad impressions per day </IconWithHint>
          </template>
        </el-table-column>
        <el-table-column
          align="center"
          label="Alexa"
          sortable="custom"
          :formatter="formatter"
          prop="rank"
          class-name="site"
          width="40"
        />
        <el-table-column
          align="center"
          label="Actions"
          class-name="site"
          :width="isMobileViewport ? 50 : ''"
        >
          <template #default="{ row }">
            <el-tooltip
              v-if="row.attributes.info.length > 0"
              class="item"
              placement="top-start"
            >
              <template #content>
                <span>
                  {{ row.attributes.info.join('.') }}
                </span>
              </template>
              <el-button
                type="info"
                size="small"
                circle
                icon="InfoFilledElementIcon"
              />
            </el-tooltip>
            <br />
            <el-tooltip
              v-if="false"
              class="item"
              placement="top-start"
              content="Request details"
            >
              <el-button
                type="primary"
                size="small"
                circle
                icon="MessageElementIcon"
                @click="showMessagePopup(row)"
              />
            </el-tooltip>
            <br />
            <el-tooltip
              v-if="row.attributes.availability"
              class="item"
              placement="top-start"
              content="Buy traffic"
            >
              <el-button
                type="success"
                size="small"
                circle
                icon="MoneyElementIcon"
                @click="showCampaignsPopup(row.id)"
              />
            </el-tooltip>
            <el-tooltip
              v-if="row.attributes.availability"
              class="item"
              placement="top-start"
              content="Offer flat deal"
            >
              <el-button
                type="warning"
                size="small"
                circle
                icon="WalletFilledElementIcon"
                @click="showFlatDealForm(row)"
              />
            </el-tooltip>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>
<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue'
import TrafficLabel from './TrafficLabel.vue'
import AvailabilityLabel from './AvailabilityLabel.vue'
import PublisherTypeLabel from './PublisherTypeLabel.vue'
import ExportButton from '@/components/ExportButton.vue'
import ListButton from './ListButton.vue'
import IconWithHint from '@/components/IconWithHint.vue'
import useResize from '@/hooks/useResize'
import globe from '@/apps/traforama/assets/icons/globe.png'

export default defineComponent({
  name: 'MarketplaceTable',
  components: {
    TrafficLabel,
    AvailabilityLabel,
    PublisherTypeLabel,
    IconWithHint,
    ExportButton,
    ListButton
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    sum: {
      type: Number,
      default: 0
    },
    total: {
      type: Number,
      default: 0
    },
    showSizeColumn: {
      type: Boolean,
      default: false
    },
    listButtonState: {
      type: Object,
      required: true
    }
  },
  setup(props, context) {
    const { isDesktop, isMobileViewport } = useResize()
    const table = ref()
    const viewType = ref('Mobile')

    const applySorting = (sort: any) => {
      if (sort.order) {
        context.emit('sort', sort.prop, sort.order)
      }
    }

    const download = (type: string) => {
      context.emit('download', type)
    }
    const openModal = () => {
      context.emit('openModal')
    }

    const showMessagePopup = (row: any) => {
      context.emit('show-chat', row.id, row.attributes)
    }

    const showCampaignsPopup = (id: string) => {
      context.emit('show-campaign-window', id)
    }

    const showFlatDealForm = (row: any) => {
      context.emit('show-deal-form', row.attributes)
    }

    const formatter = (row: any, col: any) => {
      const prop = String(col.property)
      return row.attributes[prop]
    }

    const getFlagSrc = (code: string) => {
      return code
        ? `https://flagsapi.com/${code.toUpperCase()}/flat/64.png`
        : globe
    }

    const getSummaries = ({ columns }: { columns: any }) => {
      const sums: string[] = []
      columns.forEach((column: any, index: number) => {
        if (index === 1) {
          sums[index] = 'Total'
          return
        }
        sums[index] =
          column.property === 'traffic'
            ? props.sum.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            : ''
      })

      return sums
    }

    return {
      table,
      isDesktop,
      applySorting,
      formatter,
      showMessagePopup,
      showCampaignsPopup,
      showFlatDealForm,
      download,
      viewType,
      isMobileViewport,
      getFlagSrc,
      getSummaries,
      openModal
    }
  }
})
</script>

<style lang="scss" scoped>
.el-link {
  float: right;
  padding-bottom: 5px;
}

.padding-5 {
  padding: 5px;
}

:deep(.el-card__body) {
  padding: 0 5px;
  &.mobile {
    padding: 0;
  }
}

:deep(.cell) {
  padding: 0 !important;
  margin: 0 !important;
}

:deep(.el-icon-money),
:deep(.el-icon-message),
:deep(.el-icon-info),
:deep(.el-icon-wallet) {
  color: var(--el-color-white);
  font-size: 18px;
}

.info-icons {
  .info-icon-item {
    margin: 0 10px;
    font-size: 20px;
  }
}

.mobile {
  .el-button {
    padding: 2px;
  }

  :deep(i) {
    font-size: 12px;
  }

  :deep(.site) {
    font-size: 8px;
  }

  .info-icons {
    img {
      width: 20px;
    }
  }
}
</style>
